'use client';

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCellServiceSession } from './useCellServiceSession';
import {
  pollContentModerationObfuscation,
  provenanceKeys,
} from '@/shared/cors-provenance-api';
import { invalidateFileInfoCache } from '@/shared/clientFunctions';
import { clearManualObfuscationJob } from '@/lib/manual-obfuscation-job';
import {
  isObfuscationJobFailed,
  isObfuscationPreviewReady,
} from '@/components/ui/media/media-flag-rules';

/** Re-poll cadence once the first polling window has elapsed without a result. */
const SLOW_JOB_REFETCH_MS = 15_000;

/**
 * Poll until an obfuscated preview exists for ``fileUrl``.
 * Re-reads file-info until provenance reports ``COMPLETED`` or a preview URL.
 * A blur that outlives one polling window keeps being polled, more slowly, and
 * is reported as ``timedOut`` rather than as a failure.
 */
export function usePollObfuscatedPreview(
  fileUrl: string | undefined,
  enabled: boolean,
  options?: { generation?: number; awaitingReflag?: boolean },
) {
  const { cellId, accessToken } = useCellServiceSession();
  const queryClient = useQueryClient();
  const canPoll = Boolean(enabled && fileUrl && cellId && accessToken);
  const generation = options?.generation ?? 0;
  const awaitingReflag = Boolean(options?.awaitingReflag);

  const query = useQuery({
    queryKey: [
      ...provenanceKeys.contentModeration(),
      'obfuscation-poll',
      cellId,
      fileUrl,
      generation,
    ],
    enabled: canPoll,
    queryFn: ({ signal }) =>
      pollContentModerationObfuscation({
        cellId: cellId as string,
        accessToken: accessToken as string,
        fileUrl: fileUrl as string,
        awaitingReflag,
        signal,
      }),
    staleTime: Infinity,
    retry: false,
    refetchInterval: (q) => (q.state.data?.timedOut ? SLOW_JOB_REFETCH_MS : false),
  });

  useEffect(() => {
    if (!query.isSuccess || !fileUrl) return;
    if (
      query.data?.cleared ||
      isObfuscationJobFailed(query.data?.jobStatus) ||
      isObfuscationPreviewReady({
        previewUrl: query.data?.previewUrl,
        jobStatus: query.data?.jobStatus,
      })
    ) {
      clearManualObfuscationJob(fileUrl);
    }
    invalidateFileInfoCache(fileUrl);
    queryClient.invalidateQueries({ queryKey: provenanceKeys.fileInfo() });
    queryClient.invalidateQueries({ queryKey: provenanceKeys.files() });
    queryClient.invalidateQueries({ queryKey: provenanceKeys.tree() });
    queryClient.invalidateQueries({ queryKey: provenanceKeys.contentModeration() });
  }, [query.isSuccess, query.data?.cleared, query.data?.jobStatus, query.data?.previewUrl, fileUrl, queryClient]);

  return {
    isPolling: canPoll && query.isPending,
    previewUrl: query.data?.cleared ? null : (query.data?.previewUrl ?? null),
    cleared: Boolean(query.data?.cleared),
    jobStatus: query.data?.jobStatus ?? null,
    timedOut: Boolean(query.data?.timedOut),
    failed: query.data?.jobStatus === 'FAILED' || query.isError,
  };
}
